/**
 * Needs review's telemetry (trial telemetry §5.7, docs/architecture/telemetry-catalog.md): one `review.summary` per
 * job visit (and every ten active minutes), the reason a Confirm was refused, All caught up, and a document someone
 * has been stuck on. The review screen calls the counters below; `reviewInFront` (App) says which job's review is in
 * front, and leaving it (another job, Scan jobs, Settings, Job setup, closing the window) sends what was counted.
 *
 * Near-zero cost: counters in memory, no timer (active time and the ten minutes are worked out on the person's own
 * input), fire-and-forget sends. Ids are only keys here: main turns the job into its kind and never sends an id.
 */
import { ReviewSummary, type ConfirmRefusalCode, type LineValues } from './review-summary';
import { reportEvent } from './telemetry';

type InFront = { jobId: string; summary: ReviewSummary };
let inFront: InFront | null = null;
/** What the review screen last showed, kept while its job is not in front so arriving on it counts its document. */
let lastShown: { jobId: string; documentId: string | null } | null = null;
let lastWaiting: { jobId: string; count: number } | null = null;
const caughtUpJobs = new Set<string>();

const send = (visit: InFront) => {
  const attrs = visit.summary.take();
  if (attrs) reportEvent('review.summary', { attrs, jobId: visit.jobId });
};

let listening = false;
/** The person's own input on the review: active time, the stuck document and the ten-minute summary. Once per window. */
const listen = () => {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  const input = () => {
    const visit = inFront;
    if (!visit) return;
    try {
      const due = visit.summary.input();
      if (due.longDocumentMinutes !== undefined)
        reportEvent('review.long-document', {
          attrs: { minutes: due.longDocumentMinutes },
          jobId: visit.jobId,
        });
      if (due.summaryDue) send(visit);
    } catch {
      /* telemetry never breaks the review */
    }
  };
  for (const type of ['pointerdown', 'pointermove', 'keydown', 'wheel'] as const)
    window.addEventListener(type, input, { capture: true, passive: true });
  window.addEventListener('blur', () => inFront?.summary.pause());
  // Closing the window is leaving the job: best effort, the send does not wait.
  window.addEventListener('pagehide', () => reviewInFront(null));
};

/**
 * The job whose documents are in front, or null when the person is anywhere else. A change sends the summary of the
 * job left and starts counting the one in front.
 */
export function reviewInFront(jobId: string | null): void {
  if (inFront?.jobId === (jobId ?? undefined)) return;
  try {
    if (inFront) send(inFront);
    inFront = jobId ? { jobId, summary: new ReviewSummary() } : null;
    if (!inFront) return;
    listen();
    if (lastWaiting?.jobId === jobId) inFront.summary.waiting(lastWaiting.count);
    if (lastShown?.jobId === jobId) inFront.summary.shown(lastShown.documentId);
  } catch {
    /* never thrown into the screen */
  }
}

const counting = (jobId: string) => (inFront?.jobId === jobId ? inFront.summary : null);

/** The review screen's counters. Each is a no-op unless its job's review is in front. */
export const reviewTally = {
  /** The document open as work (null: none, such as All caught up). Safe to call on every render. */
  shown(jobId: string, documentId: string | null) {
    if (lastShown?.jobId !== jobId || lastShown.documentId !== documentId)
      lastShown = { jobId, documentId };
    counting(jobId)?.shown(documentId);
  },
  /** Documents waiting in Needs review. Safe to call on every render. */
  waiting(jobId: string, count: number) {
    if (lastWaiting?.jobId !== jobId || lastWaiting.count !== count) lastWaiting = { jobId, count };
    counting(jobId)?.waiting(count);
  },
  confirmPressed(jobId: string, documentId: string) {
    counting(jobId)?.confirmPressed(documentId);
  },
  confirmed(jobId: string, documentId: string) {
    counting(jobId)?.confirmed(documentId);
  },
  fieldChanged(jobId: string, documentId: string) {
    counting(jobId)?.fieldChanged(documentId);
  },
  typeChanged(jobId: string, documentId: string) {
    counting(jobId)?.typeChanged(documentId);
  },
  lineSaved(
    jobId: string,
    documentId: string,
    before: LineValues | null,
    after: LineValues | null,
  ) {
    counting(jobId)?.lineSaved(documentId, before, after);
  },
  boundary(jobId: string, kind: 'split' | 'join', documentIds: Iterable<string>) {
    counting(jobId)?.boundary(kind, documentIds);
  },
  pageRemoved(jobId: string, documentId: string) {
    counting(jobId)?.pageRemoved(documentId);
  },
  skipped(jobId: string) {
    counting(jobId)?.skipped();
  },
  back(jobId: string) {
    counting(jobId)?.back();
  },
  /** Confirm refused, and why: counted in the summary and sent as its reason (never the words shown). */
  refused(jobId: string, code: ConfirmRefusalCode) {
    counting(jobId)?.refused();
    reportEvent(`review.confirm-refused:${code}`, { jobId });
  },
  /** All caught up after confirming here: once per job in this window (main keeps one a session). */
  caughtUp(jobId: string) {
    if (caughtUpJobs.has(jobId)) return;
    caughtUpJobs.add(jobId);
    reportEvent('review.caught-up', { jobId });
  },
};
